import React, { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getTrainingSport, TRAINING_SPORT_LIST } from '../config/trainingSports';
import { SITE_APPS, SITE_NAME, formatAppTitle, getSiteOrigin } from '../config/siteBranding';
import { setPageMeta, resetPageMeta } from '../utils/pageMeta';
import AppSwitcher from './AppSwitcher';
import './TrainingSportHome.css';

const TrainingSportHome = () => {
  const { sportId } = useParams();
  const sport = getTrainingSport(sportId);
  const { isAuthenticated, user, logout } = useAuth();

  useEffect(() => {
    if (!sport) return undefined;
    setPageMeta({
      title: formatAppTitle(`${sport.name} Training`),
      description: sport.summary,
      url: `${getSiteOrigin()}${sport.href}`,
    });
    return () => resetPageMeta();
  }, [sport]);

  if (!sport) {
    return <Navigate to="/training" replace />;
  }

  const primaryCta = sport.trackerAvailable
    ? {
        to: isAuthenticated ? sport.trackerHref : '/login',
        label: isAuthenticated ? sport.ctaLabel : 'Log in to track',
      }
    : {
        to: isAuthenticated ? '/training' : '/register',
        label: isAuthenticated ? 'Back to Training home' : sport.ctaLabel,
      };

  return (
    <div className="training-sport">
      <header className="training-sport__topbar">
        <div className="training-sport__topbar-inner">
          <div className="training-sport__brand-block">
            <Link to="/" className="training-sport__site">
              {SITE_NAME}
            </Link>
            <AppSwitcher activeAppId="training" variant="compact" />
          </div>
          <nav className="training-sport__account" aria-label="Account">
            <Link to="/training" className="training-sport__text-link">
              All sports
            </Link>
            {isAuthenticated ? (
              <>
                <span className="training-sport__user">Hi, {user?.name?.split(' ')[0] || 'there'}</span>
                <button type="button" className="training-sport__btn" onClick={logout}>
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="training-sport__text-link">
                  Log in
                </Link>
                <Link to="/register" className="training-sport__btn training-sport__btn--primary">
                  Join
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      <main className="training-sport__main">
        <p className="training-sport__eyebrow">
          <Link to="/training">{SITE_APPS.training.name}</Link>
        </p>
        <h1 className="training-sport__title">{sport.name}</h1>
        <p className="training-sport__lead">{sport.summary}</p>

        <ul className="training-sport__highlights">
          {sport.highlights.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>

        <div className="training-sport__actions">
          <Link to={primaryCta.to} className="btn btn-primary">
            {primaryCta.label}
          </Link>
          {sport.trackerAvailable && !isAuthenticated && (
            <Link to="/register" className="btn btn-secondary">
              Create account
            </Link>
          )}
        </div>

        <section className="training-sport__plans" aria-labelledby="training-plans-heading">
          <h2 id="training-plans-heading">Trainings in this sport</h2>
          <p className="training-sport__plans-intro">
            {sport.trackerAvailable
              ? 'Pick a plan in the tracker after you sign in.'
              : 'Browse what’s coming for this sport—daily tracking for running is next.'}
          </p>
          <ol className="training-sport__plan-list">
            {sport.trainings.map((training) => (
              <li key={training.id} className="training-sport__plan">
                <div className="training-sport__plan-head">
                  <h3>{training.name}</h3>
                  <span className="training-sport__level">{training.level}</span>
                </div>
                <p>{training.description}</p>
              </li>
            ))}
          </ol>
        </section>

        <nav className="training-sport__other" aria-label="Other sports">
          <h2>Other sports</h2>
          <ul>
            {TRAINING_SPORT_LIST.filter((item) => item.id !== sport.id).map((item) => (
              <li key={item.id}>
                <Link to={item.href}>{item.name}</Link>
                <span>{item.tagline}</span>
              </li>
            ))}
          </ul>
        </nav>
      </main>
    </div>
  );
};

export default TrainingSportHome;
